import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NAV_ITEMS, applyMenuOrder } from "@/lib/navItems";
import { Button } from "@/components/ui/button";
import { DotsSixVertical, ArrowUp, ArrowDown, ArrowClockwise, FloppyDisk, List } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Admin-Seite: Navigationseinträge per Drag & Drop neu sortieren. */
export default function MenuOrder() {
  const { user } = useAuth();
  const [order, setOrder] = useState([]); // list of items {to, label, icon, roles}
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    api.get("/settings/menu-order").then(r => {
      const merged = applyMenuOrder(NAV_ITEMS, r.data.order || []);
      setOrder(merged);
    }).catch(() => setOrder(NAV_ITEMS));
  }, []);

  const move = (from, to) => {
    if (from === to || to < 0 || to >= order.length) return;
    const next = order.slice();
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    setOrder(next);
    setDirty(true);
  };

  const onDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver  = (e, idx) => { e.preventDefault(); if (dragIdx !== null && dragIdx !== idx) move(dragIdx, idx); setDragIdx(idx); };
  const onDragEnd   = () => setDragIdx(null);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/menu-order", { order: order.map(i => i.to) });
      toast.success("Reihenfolge gespeichert. Wird beim nächsten Login/Reload überall aktiv.");
      setDirty(false);
      // Sofort im aktuellen Layout aktualisieren
      window.dispatchEvent(new CustomEvent("msb-menu-order-changed", { detail: order.map(i => i.to) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Reihenfolge auf Standard zurücksetzen?")) return;
    setOrder(NAV_ITEMS);
    setDirty(true);
  };

  if (user?.role !== "admin") {
    return <div className="msb-card"><p className="text-muted-foreground">Nur für Administratoren zugänglich.</p></div>;
  }

  return (
    <div className="space-y-6" data-testid="menu-order-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: "'Work Sans',sans-serif" }}>Menü anpassen</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Verschiebe Navigationspunkte per Drag &amp; Drop oder mit den Pfeiltasten. Die Reihenfolge wird für alle Nutzer gespeichert.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={resetToDefault} className="rounded-xl" data-testid="menu-reset">
            <ArrowClockwise size={14} weight="bold"/><span className="ml-1.5">Standard</span>
          </Button>
          <Button onClick={save} disabled={!dirty || saving} className="rounded-xl bg-primary text-white" data-testid="menu-save">
            <FloppyDisk size={14} weight="bold"/><span className="ml-1.5">{saving ? "Speichere…" : "Speichern"}</span>
          </Button>
        </div>
      </div>

      <div className="msb-card p-0 overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-border flex items-center gap-2 text-xs text-muted-foreground">
          <List size={14} weight="bold"/>
          <span>{order.length} Einträge · ziehe die Zeilen oder nutze die Pfeile</span>
        </div>
        <ul className="divide-y divide-border" data-testid="menu-list">
          {order.map((item, idx) => {
            const Icon = item.icon;
            const isDrag = dragIdx === idx;
            return (
              <li
                key={item.to}
                draggable
                onDragStart={(e) => onDragStart(e, idx)}
                onDragOver={(e) => onDragOver(e, idx)}
                onDragEnd={onDragEnd}
                className={`flex items-center gap-3 p-3 hover:bg-slate-50 transition ${isDrag ? "bg-primary/10 ring-2 ring-primary/40" : ""}`}
                data-testid={`menu-item-${item.to.replace(/\//g,"-") || "root"}`}
              >
                <DotsSixVertical size={22} weight="bold" className="text-slate-400 cursor-move flex-shrink-0"/>
                <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                  <Icon size={16} weight="duotone"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">{item.label}</div>
                  <div className="text-[11px] text-muted-foreground truncate">
                    {item.to} · sichtbar für: {item.roles.join(", ")}
                  </div>
                </div>
                <div className="w-8 text-xs text-muted-foreground text-right font-mono">{idx + 1}</div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => move(idx, idx - 1)}
                    disabled={idx === 0}
                    className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Nach oben"
                    data-testid={`menu-up-${idx}`}
                  ><ArrowUp size={14} weight="bold"/></button>
                  <button
                    onClick={() => move(idx, idx + 1)}
                    disabled={idx === order.length - 1}
                    className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Nach unten"
                    data-testid={`menu-down-${idx}`}
                  ><ArrowDown size={14} weight="bold"/></button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {dirty && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
          Es gibt ungespeicherte Änderungen. Klicke <b>Speichern</b>, damit die neue Reihenfolge dauerhaft übernommen wird.
        </div>
      )}
    </div>
  );
}
